import { Feature, User } from "src/types/userTypes";
import { Redirect, Route } from "react-router-dom";
import { useGetLicensedProduct, usePermissionSet } from "./rbac-rules";

import jwt_decode, { JwtPayload } from "jwt-decode";
import { nuclearn_user_token } from "src/App";
import { useGetUserDetails } from "./UserDetails";
import { ADMIN_ROLE_NAME } from "src/constants";
import { QueryClient, useQueryClient } from "react-query";
import { logoutCurrentUser } from "src/views/pages/login/Login";

export interface AuthorizedUserResult {
  Authorization?: string;
  invalid?: boolean; // token exists but invalid or expired
}

export const stripBearerPrefix = (token: string): string =>
  token.replace(/^bearer\s+/i, "");

export function authorizedUser(): AuthorizedUserResult {
  const token = nuclearn_user_token
    ? localStorage.getItem(nuclearn_user_token)
    : null;
  if (!token) return {};
  try {
    const decoded = jwt_decode<JwtPayload>(token);
    const expired = decoded?.exp && decoded.exp < Math.floor(Date.now() / 1000);
    if (!expired) {
      return { Authorization: token };
    }
    return { invalid: true };
  } catch (_e) {
    return { invalid: true };
  }
}

// Imperative redirect util (no hooks) to be used when we detect invalid auth in non-React contexts.
export function redirectToLogin(preservePath: boolean = true) {
  if (preservePath) {
    try {
      sessionStorage.setItem("redirectUrl", window.location.pathname);
    } catch {}
  }
  window.location.replace("/login");
}

export function isAuthorizedUser() {
  const auth = authorizedUser();
  return !!auth.Authorization;
}

/*
 * A wrapper for <Route> that redirects to the login
 * screen if you're not yet authenticated.
 */
interface PrivateRouteProps {
  children: React.ReactNode;
}

export function PrivateRoute({ children, ...rest }: PrivateRouteProps) {
  let auth = isAuthorizedUser();

  return (
    // @ts-ignore
    <Route
      {...rest}
      // @ts-ignore
      render={({ location }) => {
        if (auth) {
          return children;
        } else {
          sessionStorage.setItem("redirectUrl", location.pathname);
          return (
            // @ts-ignore
            <Redirect
              to={{
                pathname: "/login",
              }}
            />
          );
        }
      }}
    />
  );
}

const check = ({
  action,
  userDetails,
  queryClient,
}: {
  action: string;
  userDetails: User | null;
  queryClient: QueryClient;
}) => {
  //* LEGACY CODE HANDLING: v1.19.0 changed from single role to array of roles
  // If user still has old format stored, force logout to refresh their credentials
  if (userDetails?.role) {
    logoutCurrentUser(queryClient);
    console.log("Logging out user with legacy role format:", userDetails);
    return true;
  }
  if (!userDetails) {
    // Authentication check failed - user not logged in
    return false;
  }

  // Admins have universal access - bypass permission checks
  let IS_ADMIN = userDetails.roles.find(
    (user) => user.name === ADMIN_ROLE_NAME,
  );

  if (IS_ADMIN) {
    return true;
  }
  // check if the user has the action in their role
  if (action === undefined) {
    console.error("No action provided to check permission", action);
    return false;
  }

  // Parse the action string into feature and privilege components
  // Format: "featureName:privilegeType" (e.g., "users:read")
  let [feature, privilege] = action.split(":");

  const privileges =
    userDetails?.roles.map((role) => role.privileges).flat() || [];

  // Extract all features from the user's roles and privileges
  const featuresList = privileges
    .map((privilege) => privilege.feature)
    .filter((feat) => feat.name);

  // Find if user has the requested feature
  const matchedFeature = featuresList.find(
    (feat: Feature) => feat.name?.toLowerCase() === feature?.toLowerCase(),
  );

  if (
    matchedFeature &&
    privileges
      .filter((priv) => priv.feature_fk === matchedFeature.id)
      .filter((priv) => (priv as any)[`${privilege}`] === true).length
  ) {
    return true;
  }
  return false;
};

export const usePermission = (
  perform: string,
  product: string | string[] | null = null,
) => {
  /*
   * Check user permissions - returns true if user can, false if they cannot
   * Check license permissions - returns true if user has license, false if they do not
   */

  // if product is a string, make it an array
  let productArray: string[] = [];
  if (product) {
    if (typeof product === "string") {
      productArray = [product];
    } else {
      productArray = product;
    }
  }

  const {
    data: rules,
    isLoading: rulesLoading,
    isError: rulesLoadError,
    isSuccess: rulesLoadSuccess,
  } = usePermissionSet();

  const {
    data: licensedProduct,
    isLoading: licensedProductLoading,
    isError: licensedProductLoadError,
    isSuccess: licensedProductLoadSuccess,
  } = useGetLicensedProduct();

  const userDetails = useGetUserDetails();
  const queryClient = useQueryClient();

  let rulesOk =
    !rulesLoading &&
    rulesLoadSuccess &&
    rules &&
    check({
      action: perform,
      userDetails: userDetails,
      queryClient: queryClient,
    });

  let licensedProductOk =
    (!licensedProductLoading &&
      licensedProductLoadSuccess &&
      licensedProduct &&
      Array.isArray(licensedProduct) &&
      licensedProduct.filter((value) => productArray.includes(value)).length >
        0) ||
    !product;

  let res = {
    canIt: rulesOk && licensedProductOk,
    isLoading: rulesLoading || (licensedProductLoading && product),
    rulesLoadError: !rulesLoading && rulesLoadError,
    licenseLoadError: !licensedProductLoading && licensedProductLoadError,
  };

  return res;
};

/**
 * Like usePermission, but collapses `canIt`/`isLoading` into a single
 * "definitively denied" boolean — false both when permitted and while the
 * permission rules are still loading. Prefer this over destructuring
 * usePermission directly whenever the caller can't use <Can> (e.g. gating an
 * imperative handler, or a JSX branch with existing complex conditionals):
 * checking `!canIt` alone treats an unresolved check as a denial, flashing a
 * false "no permission" state for an authorized user on first render, and
 * threading isLoading through separately is easy to leave out of a
 * useCallback dependency array by mistake (see #2497 review history).
 */
export const useIsPermissionDenied = (
  perform: string,
  product: string | string[] | null = null,
): boolean => {
  const { canIt, isLoading } = usePermission(perform, product);
  return !isLoading && !canIt;
};

interface CanProps {
  yes: (() => React.ReactNode) | React.ReactNode | string;
  no?: (() => React.ReactNode) | React.ReactNode | string;
  perform: string;
  product?: string | string[];
}

export const Can = ({
  yes = () => null,
  no = () => null,
  perform,
  product,
}: CanProps) => {
  const { canIt, isLoading } = usePermission(perform, product);

  if (isLoading) {
    return <></>;
  }

  return (
    <>
      {canIt
        ? typeof yes === "function"
          ? yes()
          : yes
        : typeof no === "function"
          ? no()
          : no}
    </>
  );
};
